import React, { useState, useEffect } from 'react';
import { safeAsync, Badge, KPI, neuShadow, neuShadowSm, neuShadowInset, inp, btnPrimary, btnSm, btnDanger, cardWrap, emptyMsg, overlayStyle, modalStyle, modalHeader, modalTitle, closeBtn } from './crmShared';
import { Plus, X, Eye, TrendingUp, Send, Trash2 } from 'lucide-react';

// Same-origin relative path. In production the React bundle is served from
// portal.zignagenix.com and the backend lives at the same origin under /api.
// In local dev, package.json's `proxy` field forwards /api/* from the React
// dev server (3000) to the Express backend (3001). Hardcoding localhost:3001
// would only ever work on a developer's own machine.
const API_BASE = '/api';

// REST endpoints used here (/api/crm/funnels, /api/crm/forms, etc.) are not
// yet implemented on the Express backend — the canonical pattern is the IPC
// bridge (POST /api/ipc { channel, args }) per CLAUDE.md. Until these are
// either wired through that bridge or stood up as REST routes, the tab
// degrades to an empty state instead of throwing a blocking alert. This
// helper returns null when the response isn't JSON (the SPA's index.html
// fallback is the usual culprit) so callers can render the empty state.
async function _parseJsonOrNull(res) {
  if (!res || !res.ok) return null;
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) return null;
  try { return await res.json(); } catch { return null; }
}

export default function FunnelsTab({ canCreate, canUpdate }) {
  const [tab, setTab] = useState('funnels');
  const [funnels, setFunnels] = useState([]);
  const [forms, setForms] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFunnelModal, setShowFunnelModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingFunnel, setEditingFunnel] = useState(null);
  const [editingForm, setEditingForm] = useState(null);
  const [funnelData, setFunnelData] = useState({ name: '', type: 'lead_capture', description: '' });
  const [formData, setFormData] = useState({ name: '', type: 'contact', fields: [], submit_btn_text: 'Submit', success_message: 'Thank you!', redirect_url: '', create_contact: false, assign_tag: '', assign_pipeline_stage: '' });
  const [newField, setNewField] = useState({ label: '', type: 'text', required: false, placeholder: '' });

  useEffect(() => {
    if (tab === 'funnels') loadFunnels();
    if (tab === 'forms') loadForms();
    if (tab === 'submissions' && selectedForm) loadSubmissions();
  }, [tab, selectedForm]);

  const loadFunnels = safeAsync(async () => {
    setLoading(true);
    const res = await fetch(`${API_BASE}/crm/funnels`);
    const data = await _parseJsonOrNull(res);
    setFunnels(Array.isArray(data) ? data : []);
    setLoading(false);
  });

  const loadForms = safeAsync(async () => {
    setLoading(true);
    const res = await fetch(`${API_BASE}/crm/forms`);
    const data = await _parseJsonOrNull(res);
    setForms(Array.isArray(data) ? data : []);
    setLoading(false);
  });

  const loadSubmissions = safeAsync(async () => {
    if (!selectedForm) return;
    setLoading(true);
    const res = await fetch(`${API_BASE}/crm/forms/${selectedForm}/submissions`);
    const data = await _parseJsonOrNull(res);
    setSubmissions(Array.isArray(data) ? data : []);
    setLoading(false);
  });

  const handleCreateFunnel = safeAsync(async () => {
    if (!funnelData.name.trim()) return alert('Name required');
    const payload = editingFunnel ? { id: editingFunnel, ...funnelData } : funnelData;
    const method = editingFunnel ? 'PATCH' : 'POST';
    const url = editingFunnel ? `${API_BASE}/crm/funnels/${editingFunnel}` : `${API_BASE}/crm/funnels`;
    const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    if (res.ok) {
      loadFunnels();
      setShowFunnelModal(false);
      setEditingFunnel(null);
      setFunnelData({ name: '', type: 'lead_capture', description: '' });
    }
  });

  const handleCreateForm = safeAsync(async () => {
    if (!formData.name.trim()) return alert('Name required');
    if (formData.fields.length === 0) return alert('Add at least one field');
    const payload = editingForm ? { id: editingForm, ...formData } : formData;
    const method = editingForm ? 'PATCH' : 'POST';
    const url = editingForm ? `${API_BASE}/crm/forms/${editingForm}` : `${API_BASE}/crm/forms`;
    const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    if (res.ok) {
      loadForms();
      setShowFormModal(false);
      setEditingForm(null);
      setFormData({ name: '', type: 'contact', fields: [], submit_btn_text: 'Submit', success_message: 'Thank you!', redirect_url: '', create_contact: false, assign_tag: '', assign_pipeline_stage: '' });
    }
  });

  const handleDeleteFunnel = safeAsync(async (id) => {
    if (!window.confirm('Delete funnel?')) return;
    await fetch(`${API_BASE}/crm/funnels/${id}`, { method: 'DELETE' });
    loadFunnels();
  });

  const handleDeleteForm = safeAsync(async (id) => {
    if (!window.confirm('Delete form?')) return;
    await fetch(`${API_BASE}/crm/forms/${id}`, { method: 'DELETE' });
    loadForms();
  });

  const addField = () => {
    if (!newField.label.trim()) return alert('Field label required');
    setFormData(f => ({ ...f, fields: [...f.fields, { ...newField, id: Date.now() }] }));
    setNewField({ label: '', type: 'text', required: false, placeholder: '' });
  };

  const removeField = (id) => {
    setFormData(f => ({ ...f, fields: f.fields.filter(fld => fld.id !== id) }));
  };

  const toggleFormActive = safeAsync(async (formId, active) => {
    const res = await fetch(`${API_BASE}/crm/forms/${formId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ active: !active }) });
    if (res.ok) loadForms();
  });

  const openEditFunnel = (funnel) => {
    setEditingFunnel(funnel.id);
    setFunnelData({ name: funnel.name, type: funnel.type, description: funnel.description || '' });
    setShowFunnelModal(true);
  };

  const openEditForm = (form) => {
    setEditingForm(form.id);
    setFormData({ name: form.name, type: form.type, fields: form.fields || [], submit_btn_text: form.submit_btn_text || 'Submit', success_message: form.success_message || 'Thank you!', redirect_url: form.redirect_url || '', create_contact: form.create_contact || false, assign_tag: form.assign_tag || '', assign_pipeline_stage: form.assign_pipeline_stage || '' });
    setShowFormModal(true);
  };

  const typeColor = (type) => {
    const colors = { lead_capture: '#2E8B98', sales: '#62BB46', booking: '#F26522', survey: '#256F7A', contact: '#4FA038' };
    return colors[type] || '#6C757D';
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #E9ECEF', marginBottom: 24 }}>
        {['funnels', 'forms', 'submissions'].map(t => (
          <button key={t} onClick={() => { setTab(t); if (t === 'submissions' && !selectedForm) setSelectedForm(null); }} style={{ padding: '12px 20px', border: 'none', background: 'none', borderBottom: tab === t ? '3px solid #2E8B98' : 'none', color: tab === t ? '#2E8B98' : '#6C757D', fontWeight: tab === t ? 700 : 500, fontSize: 13, cursor: 'pointer', textTransform: 'capitalize' }}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'funnels' && (
        <div>
          {canCreate && (
            <button onClick={() => { setEditingFunnel(null); setFunnelData({ name: '', type: 'lead_capture', description: '' }); setShowFunnelModal(true); }} style={btnPrimary}>
              <Plus size={14} /> Create Funnel
            </button>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16, marginTop: 20 }}>
            {funnels.length === 0 ? (
              <div style={{ ...emptyMsg, gridColumn: '1/-1' }}>No funnels created</div>
            ) : (
              funnels.map(f => (
                <div key={f.id} style={{ ...cardWrap, padding: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
                    <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0, color: '#212529' }}>{f.name}</h3>
                    {canUpdate && (
                      <button onClick={() => handleDeleteFunnel(f.id)} style={btnDanger}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <Badge text={f.type.replace('_', ' ').toUpperCase()} color={typeColor(f.type)} />
                  </div>
                  <div style={{ marginBottom: 14, color: '#6C757D', fontSize: 12 }}>{f.description}</div>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <KPI icon={Eye} label="Views" value={f.views || 0} color="#2E8B98" />
                    <KPI icon={TrendingUp} label="Conversions" value={f.conversions || 0} color="#62BB46" sub={f.conversion_rate ? `${f.conversion_rate}%` : '0%'} />
                  </div>
                  {canUpdate && (
                    <button onClick={() => openEditFunnel(f)} style={{ ...btnSm, width: '100%', marginTop: 12, justifyContent: 'center' }}>Edit</button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {tab === 'forms' && (
        <div>
          {canCreate && (
            <button onClick={() => { setEditingForm(null); setFormData({ name: '', type: 'contact', fields: [], submit_btn_text: 'Submit', success_message: 'Thank you!', redirect_url: '', create_contact: false, assign_tag: '', assign_pipeline_stage: '' }); setNewField({ label: '', type: 'text', required: false, placeholder: '' }); setShowFormModal(true); }} style={btnPrimary}>
              <Plus size={14} /> Create Form
            </button>
          )}
          <div style={{ marginTop: 20 }}>
            {forms.length === 0 ? (
              <div style={emptyMsg}>No forms created</div>
            ) : (
              <div style={{ ...cardWrap, overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #E9ECEF', background: '#F0F4F8' }}>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6C757D' }}>Name</th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6C757D' }}>Type</th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6C757D' }}>Submissions</th>
                      <th style={{ padding: 12, textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#6C757D' }}>Active</th>
                      <th style={{ padding: 12, textAlign: 'right', fontSize: 12, fontWeight: 600, color: '#6C757D' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {forms.map(f => (
                      <tr key={f.id} style={{ borderBottom: '1px solid #E9ECEF' }}>
                        <td style={{ padding: 12, fontSize: 13, color: '#212529' }}>{f.name}</td>
                        <td style={{ padding: 12, fontSize: 13 }}><Badge text={f.type} color={typeColor(f.type)} /></td>
                        <td style={{ padding: 12, fontSize: 13, color: '#212529' }}>{f.submission_count || 0}</td>
                        <td style={{ padding: 12, textAlign: 'center' }}>
                          <input type="checkbox" checked={f.active || false} onChange={() => toggleFormActive(f.id, f.active)} style={{ cursor: 'pointer' }} />
                        </td>
                        <td style={{ padding: 12, textAlign: 'right', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          {canUpdate && (
                            <>
                              <button onClick={() => openEditForm(f)} style={btnSm}>Edit</button>
                              <button onClick={() => handleDeleteForm(f.id)} style={btnDanger}><Trash2 size={12} /></button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'submissions' && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <select value={selectedForm || ''} onChange={(e) => setSelectedForm(e.target.value || null)} style={inp}>
              <option value="">Select Form...</option>
              {forms.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>
          {selectedForm && (
            <div>
              {submissions.length === 0 ? (
                <div style={emptyMsg}>No submissions yet</div>
              ) : (
                <div style={{ ...cardWrap, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #E9ECEF', background: '#F0F4F8' }}>
                        <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6C757D' }}>Date</th>
                        <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6C757D' }}>Source</th>
                        <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6C757D' }}>Data</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submissions.map(s => (
                        <tr key={s.id} style={{ borderBottom: '1px solid #E9ECEF' }}>
                          <td style={{ padding: 12, fontSize: 12, color: '#6C757D' }}>{new Date(s.created_at).toLocaleString()}</td>
                          <td style={{ padding: 12, fontSize: 12, color: '#212529' }}>{s.source || 'web'}</td>
                          <td style={{ padding: 12, fontSize: 12, color: '#212529', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{JSON.stringify(s.data)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {showFunnelModal && (
        <div style={overlayStyle} onClick={() => setShowFunnelModal(false)}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <div style={modalHeader}>
              <h2 style={modalTitle}>{editingFunnel ? 'Edit Funnel' : 'Create Funnel'}</h2>
              <button onClick={() => setShowFunnelModal(false)} style={closeBtn}><X size={16} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6C757D', marginBottom: 4, display: 'block' }}>Name</label>
                <input type="text" value={funnelData.name} onChange={(e) => setFunnelData({ ...funnelData, name: e.target.value })} style={inp} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6C757D', marginBottom: 4, display: 'block' }}>Type</label>
                <select value={funnelData.type} onChange={(e) => setFunnelData({ ...funnelData, type: e.target.value })} style={inp}>
                  <option value="lead_capture">Lead Capture</option>
                  <option value="sales">Sales</option>
                  <option value="booking">Booking</option>
                  <option value="survey">Survey</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6C757D', marginBottom: 4, display: 'block' }}>Description</label>
                <textarea value={funnelData.description} onChange={(e) => setFunnelData({ ...funnelData, description: e.target.value })} style={{ ...inp, minHeight: 60, resize: 'vertical' }} />
              </div>
              <button onClick={handleCreateFunnel} style={{ ...btnPrimary, width: '100%', justifyContent: 'center' }}>
                {editingFunnel ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFormModal && (
        <div style={overlayStyle} onClick={() => setShowFormModal(false)}>
          <div style={{ ...modalStyle, width: 640, maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
            <div style={modalHeader}>
              <h2 style={modalTitle}>{editingForm ? 'Edit Form' : 'Create Form'}</h2>
              <button onClick={() => setShowFormModal(false)} style={closeBtn}><X size={16} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6C757D', marginBottom: 4, display: 'block' }}>Form Name</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} style={inp} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6C757D', marginBottom: 4, display: 'block' }}>Type</label>
                <select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} style={inp}>
                  <option value="contact">Contact</option>
                  <option value="lead">Lead</option>
                  <option value="survey">Survey</option>
                </select>
              </div>

              <div style={{ borderTop: '1px solid #E9ECEF', paddingTop: 12 }}>
                <h3 style={{ fontSize: 12, fontWeight: 700, marginBottom: 12, color: '#212529' }}>Form Fields</h3>
                {formData.fields.map(fld => (
                  <div key={fld.id} style={{ background: '#FFF', padding: 12, borderRadius: 8, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: neuShadowInset }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#212529' }}>{fld.label}</div>
                      <div style={{ fontSize: 11, color: '#6C757D' }}>{fld.type}{fld.required ? ' • Required' : ''}</div>
                    </div>
                    <button onClick={() => removeField(fld.id)} style={btnDanger}><X size={12} /></button>
                  </div>
                ))}
                <div style={{ background: '#F0F4F8', padding: 12, borderRadius: 8, marginBottom: 12 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                    <input type="text" placeholder="Field label" value={newField.label} onChange={(e) => setNewField({ ...newField, label: e.target.value })} style={inp} />
                    <select value={newField.type} onChange={(e) => setNewField({ ...newField, type: e.target.value })} style={inp}>
                      <option value="text">Text</option>
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                      <option value="select">Select</option>
                      <option value="textarea">Textarea</option>
                      <option value="date">Date</option>
                      <option value="checkbox">Checkbox</option>
                      <option value="number">Number</option>
                    </select>
                  </div>
                  <input type="text" placeholder="Placeholder" value={newField.placeholder} onChange={(e) => setNewField({ ...newField, placeholder: e.target.value })} style={inp} />
                  <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <label style={{ fontSize: 12, color: '#6C757D', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="checkbox" checked={newField.required} onChange={(e) => setNewField({ ...newField, required: e.target.checked })} />
                      Required
                    </label>
                    <button onClick={addField} style={{ ...btnSm, marginLeft: 'auto' }}>Add Field</button>
                  </div>
                </div>
              </div>

              <div style={{ borderTop: '1px solid #E9ECEF', paddingTop: 12 }}>
                <h3 style={{ fontSize: 12, fontWeight: 700, marginBottom: 12, color: '#212529' }}>Settings</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 2, display: 'block' }}>Submit Button Text</label>
                    <input type="text" value={formData.submit_btn_text} onChange={(e) => setFormData({ ...formData, submit_btn_text: e.target.value })} style={inp} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 2, display: 'block' }}>Success Message</label>
                    <input type="text" value={formData.success_message} onChange={(e) => setFormData({ ...formData, success_message: e.target.value })} style={inp} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 2, display: 'block' }}>Redirect URL</label>
                    <input type="text" value={formData.redirect_url} onChange={(e) => setFormData({ ...formData, redirect_url: e.target.value })} style={inp} />
                  </div>
                  <label style={{ fontSize: 12, color: '#6C757D', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="checkbox" checked={formData.create_contact} onChange={(e) => setFormData({ ...formData, create_contact: e.target.checked })} />
                    Create Contact on Submit
                  </label>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 2, display: 'block' }}>Assign Tag</label>
                    <input type="text" placeholder="tag1, tag2" value={formData.assign_tag} onChange={(e) => setFormData({ ...formData, assign_tag: e.target.value })} style={inp} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 2, display: 'block' }}>Assign Pipeline Stage</label>
                    <input type="text" value={formData.assign_pipeline_stage} onChange={(e) => setFormData({ ...formData, assign_pipeline_stage: e.target.value })} style={inp} />
                  </div>
                </div>
              </div>

              <button onClick={handleCreateForm} style={{ ...btnPrimary, width: '100%', justifyContent: 'center' }}>
                {editingForm ? 'Update Form' : 'Create Form'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
